import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { ChatMessagesDto } from '../models/chatMessage';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 217 — the global Sales Bible team channel sentinel (open to all roles). Project
// chats (item 193) key on the project id; the company↔accountant thread (item 99) passes
// no key (backend ThreadKey null).
export const TEAM_THREAD_KEY = 'team';

// item 99/193/217 — the append-only chat thread, scoped by `threadKey`:
//  - undefined → the company↔accountant thread (item 99, unchanged)
//  - a project id → that project's chat (item 193)
//  - TEAM_THREAD_KEY → the dashboard Sales Bible team channel (item 217)
// Polled every few seconds for near-real-time updates (no websockets); posting appends
// and refetches. react-query keys are scoped per thread so threads don't share a cache.
const useChat = (threadKey?: string) => {
  const queryClient = useQueryClient();
  const queryKey = threadKey ? ['chat', threadKey] : ['chat'];
  const params = threadKey ? { threadKey } : {};

  const query = useQuery(
    queryKey,
    async (): Promise<ChatMessagesDto> => {
      const { data } = await axios.get(`${apiUrl()}Chat`, { params });
      return data;
    },
    // item 966 — 5s polling on a 10-DTU DB (the dashboard card mounts this everywhere) was
    // a constant background tax; 12s is imperceptible for team chat.
    { refetchInterval: 12_000, refetchOnWindowFocus: true },
  );

  const post = useMutation(
    (vars: { body: string; attachmentId?: string }) => axios.post(`${apiUrl()}Chat`, { ...vars, threadKey }),
    { onSettled: async () => queryClient.invalidateQueries(queryKey) },
  );

  return {
    messages: query.data?.index ?? [],
    status: query.status,
    // item 99 — optional file attachment (a StoredFile id)
    send: (body: string, attachmentId?: string) => post.mutateAsync({ body, attachmentId }),
    isSending: post.isLoading,
  };
};

export default useChat;
